import { z } from 'zod';
import { WeeklyScheduleSchema } from "./weeklyScheduleSchema";
import { isOpenNow } from "@lib/utils/isOpenNow";
import { summarizeWeeklySchedule } from "@lib/utils/isOpenNow";

// export type DateRangeValueType2 = {
//     start?: number;
//     end?: number;
// }
// export type DateTimeProp2 = undefined | {
//     weeklyScheduleValue?: WeeklySchedule,
//     dateRangeValue?: DateRangeValueType2
// }


export const DateRangeValueTypeSchema = z.object({
    start: z.number().optional(), // timestamp
    end: z.number().optional() // timestamp
});

// if undefined, not enabled
// weeklyScheduleValue and dateRangeValue can be used independently or together
export const DateTimePropSchema = z.union([
    z.undefined(),
    z.object({
        weeklyScheduleValue: WeeklyScheduleSchema.optional(),
        dateRangeValue: DateRangeValueTypeSchema.optional()
    })
]);

export type DateRangeValueType = z.infer<typeof DateRangeValueTypeSchema>;
export type DateTimeProp = z.infer<typeof DateTimePropSchema>;

export const isDateTimePropActiveNow = (
    dateTimeProp: DateTimeProp,
    timezone?: string
) => {
    if (!dateTimeProp) {
        return true;
    }

    let isWithinWeeklySchedule = true;
    let isWithinDateRange = true;

    // Check weekly schedule if present
    if (dateTimeProp.weeklyScheduleValue) {
        if (!timezone) {
            timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        }
        isWithinWeeklySchedule = isOpenNow(timezone, {
            isActive: true,
            days: dateTimeProp.weeklyScheduleValue
        });
    }

    // Check date range if present
    if (dateTimeProp.dateRangeValue) {
        const now = new Date();
        const start = new Date(dateTimeProp.dateRangeValue?.start ?? 0);
        const end = new Date(dateTimeProp.dateRangeValue?.end ?? 0);
        isWithinDateRange = now >= start && now <= end;
    }

    // Both conditions must be true (if both are present)
    return isWithinWeeklySchedule && isWithinDateRange;
}

export const summarizeDateTimeProp = (dateTimeProp: DateTimeProp, locale?: string): string => {
    if (!dateTimeProp) {
        return "Always available";
    }

    const hasWeeklySchedule = !!dateTimeProp.weeklyScheduleValue;
    const hasDateRange = !!dateTimeProp.dateRangeValue;

    if (!hasWeeklySchedule && !hasDateRange) {
        return "Always available";
    }

    let summary = "";

    if (hasWeeklySchedule) {
        const scheduleLines = summarizeWeeklySchedule(dateTimeProp.weeklyScheduleValue!, locale);
        summary += `Available during scheduled hours:\n${scheduleLines.join('\n')}`;
    }

    if (hasDateRange) {
        const startDate = dateTimeProp.dateRangeValue!.start
            ? new Date(dateTimeProp.dateRangeValue!.start).toLocaleString(locale, {
                dateStyle: 'medium',
                timeStyle: 'short'
            })
            : "No start date";
        const endDate = dateTimeProp.dateRangeValue!.end
            ? new Date(dateTimeProp.dateRangeValue!.end).toLocaleString(locale, {
                dateStyle: 'medium',
                timeStyle: 'short'
            })
            : "No end date";

        const dateRangeSummary = `Available from ${startDate} to ${endDate}`;
        summary = summary ? `${summary}\n${dateRangeSummary}` : dateRangeSummary;
    }

    if (hasWeeklySchedule && hasDateRange) {
        summary = `${summary}\n(Both weekly schedule and date range must be satisfied)`;
    }

    return summary || "Availability not specified";
}

export const DEFAULT_HOLIDAY_NOTICE = "Temporarily inactive";

export const getHolidayNoticeText = (
    holiday?: { textNotice?: string | null } | null
): string => {
    // const trimmed = holiday?.textNotice?.trim();
    // return DEFAULT_HOLIDAY_NOTICE + (trimmed ? `: ${trimmed}` : "");
    return holiday?DEFAULT_HOLIDAY_NOTICE:DEFAULT_HOLIDAY_NOTICE;
};

export const isHolidayNoticeActive = (
    holiday?: {
        isEnabled?: boolean | null;
        dateRanges?: DateRangeValueType[] | null;
    } | null,
    atMs: number = Date.now()
): boolean => {
    if (!holiday || !holiday?.isEnabled) {
        return false;
    }
    const ranges = holiday.dateRanges;
    if (!ranges || ranges.length === 0) {
        return true;
    }
    return ranges.some((range) => {
        const start = range.start ?? 0;
        const end = range.end ?? Number.MAX_SAFE_INTEGER;
        return atMs >= start && atMs <= end;
    });
}